import type { ApiResponse } from 'apisauce';
import type { ResponseModel } from '../../shared/interfaces/response-model.interface';
import type { ErrorInfo } from '../../shared/interfaces/error-info.interface';
import type { UserModel } from '../../shared/models/user.model';
import type { UserType } from '../../shared/enums/user-type.enum';
import api from '../../shared/services/utils/api/api.service';
import { DISABLED_ROLE } from '../../shared/config/staff-roles.config';
import errorHelper from '../../shared/services/utils/api/error-helper.service';
import { getModuleName } from '../../UserModule/shared/services/user.service';

export interface UserAccountsQuery {
  // Plain strings, not one of the role enums. The frontend has two that disagree (UserRole with
  // 31 values, UserType with 25) and the authenticator validates against a third; the wire
  // format is strings, so the enum that matters is the API's.
  roles: string[];
  name?: string;
  email?: string;
  phone?: string;
}

export interface UserAccountsPage {
  users: UserModel[];
  count: number;
  /**
   * Whether the API actually applied the page window.
   *
   * An API without pagination support returns no count and ignores offset/limit, so `users` is
   * the entire result set rather than one page. The caller pages that itself instead of showing
   * no pager at all, and switches to server paging the moment a count comes back.
   */
  paginatedByServer: boolean;
}

/**
 * The query fields the API takes are exact-match $in lists for email and phone, and a partial
 * match for name. Only send the ones that were filled in — an empty array would match nothing.
 */
function toApiQuery(query: UserAccountsQuery) {
  return {
    role: (query.roles as unknown) as UserType[],
    ...(query.name ? { name: query.name } : {}),
    ...(query.email ? { email: [query.email.toLowerCase()] } : {}),
    ...(query.phone ? { phone: [query.phone] } : {})
  };
}

/**
 * Read a page response, working out whether the API actually paged it.
 *
 * Exported for the tests: the alternative is mocking apisauce to assert a two-line mapping.
 */
export function toUserAccountsPage(
  users: UserModel[],
  serverCount: number | undefined
): UserAccountsPage {
  const paginatedByServer = typeof serverCount === 'number';
  return {
    users,
    count: paginatedByServer ? (serverCount as number) : users.length,
    paginatedByServer
  };
}

/**
 * The rows to show for the current page: the server's answer as-is, or our own slice of the
 * full set when the server ignored the page window.
 *
 * @param page 1-based, as the Pagination component reports it.
 */
export function pageRows<T>(
  rows: T[],
  page: number,
  limit: number,
  paginatedByServer: boolean
): T[] {
  if (paginatedByServer) {
    return rows;
  }
  const start = Math.max(0, (page - 1) * limit);
  return rows.slice(start, start + limit);
}

/**
 * @param page 1-based, as the Pagination component reports it. The API takes a record offset.
 */
export function getUserAccounts(
  query: UserAccountsQuery,
  page: number,
  limit: number
): Promise<UserAccountsPage | ErrorInfo> {
  const offset = Math.max(0, (page - 1) * limit);
  return api
    .searchUser(toApiQuery(query), { offset, limit })
    .then((response: ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>) => {
      if (response.ok && response.data?.data) {
        return toUserAccountsPage(response.data.data, response.data.count);
      }
      return errorHelper(
        Number(response.status),
        String((response.data as any)?.errorCode || response.problem || ''),
        getModuleName(),
        (api.searchUser as any).displayName
      );
    });
}

/**
 * Every match, ignoring the page window — the API drops offset/limit when export is set.
 */
export function exportUserAccounts(query: UserAccountsQuery): Promise<UserModel[] | ErrorInfo> {
  return api
    .searchUser(toApiQuery(query), { export: true })
    .then((response: ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>) => {
      if (response.ok && response.data?.data) {
        return response.data.data;
      }
      return errorHelper(
        Number(response.status),
        String((response.data as any)?.errorCode || response.problem || ''),
        getModuleName(),
        (api.searchUser as any).displayName
      );
    });
}

/**
 * Revoke a staff account's access by moving it to the guest role.
 *
 * Both `role` and `roles` are set. Every access check in the platform passes if EITHER matches —
 * admin-connect's authorization middleware and the frontend's route guard both read `role` first
 * and then fall back to `roles` — so demoting only the singular field would leave the account
 * fully privileged through the array and the "disable" would be cosmetic.
 *
 * phone is deliberately not sent: admin-connect rejects an update carrying one.
 */
export function disableStaffAccess(userId: string): Promise<UserModel | ErrorInfo> {
  return api
    .updateUser({
      id: userId,
      role: (DISABLED_ROLE as unknown) as UserModel['role'],
      roles: ([DISABLED_ROLE] as unknown) as UserModel['roles']
    })
    .then((response: ApiResponse<ResponseModel<UserModel>, ErrorInfo>) => {
      if (response.ok && response.data?.data) {
        return response.data.data;
      }
      return errorHelper(
        Number(response.status),
        String((response.data as any)?.errorCode || response.problem || ''),
        getModuleName(),
        (api.updateUser as any).displayName
      );
    });
}
